import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import { Box, Button, Chip, Link, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material';
import { DEFAULT_DROPBOX_ROOT } from 'dashboard409a/components/OperationsModal/dropbox.constants';
import type { DropboxFolderOption } from 'dashboard409a/components/OperationsModal/dropbox.types';
import { getDropboxFolderStatusConfig, resolveDropboxPath, stripDropboxRootPrefix } from 'dashboard409a/components/OperationsModal/dropbox.utils';
import DropboxAuthRequired from 'dashboard409a/components/OperationsModal/DropboxAuthRequired';
import DropboxFolderSearchResults from 'dashboard409a/components/OperationsModal/DropboxFolderSearchResults';
import { ConditionalLoader, Section } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { Logger } from 'utilities';
import AttioNotesButton from './AttioNotesButton';
import { CategoryBadge, ContactSection, Container, HeaderRow, InfoRow, StyledLinkSpan } from './ValuedEntity.styles';
import { ValuedEntityProps } from './ValuedEntity.types';

const ValuedEntity = ({
  valuedEntity,
  isLoading,
  companyFolder,
  dropboxRoot = DEFAULT_DROPBOX_ROOT,
  dropboxRootOptions = [DEFAULT_DROPBOX_ROOT],
  onDropboxRootChange,
  companyFolderStatus = 'idle',
  companyFolderMatchPaths = [],
  companyFolderSimilarMatchesCount = 0,
  companyFolderSuggestions = [],
  suggestedCompanyFolderName,
  dropboxConnected,
  onDropboxConnect,
  onSearchCompanyFolder,
  onCreateCompanyFolder,
  onUpdateCompanyFolder,
}: ValuedEntityProps) => {
  const [isEditingCompanyFolder, setIsEditingCompanyFolder] = useState(false);
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [companyFolderValue, setCompanyFolderValue] = useState(companyFolder || '');
  const [isSavingCompanyFolder, setIsSavingCompanyFolder] = useState(false);
  const [selectedCompanyFolderPath, setSelectedCompanyFolderPath] = useState<string | null>(null);
  const [noticeAnchorEl, setNoticeAnchorEl] = useState<HTMLElement | null>(null);
  const noticeTextRef = useRef<HTMLSpanElement>(null);
  const [isNoticeTruncated, setIsNoticeTruncated] = useState(false);
  const [contactNoticeAnchorEl, setContactNoticeAnchorEl] = useState<HTMLElement | null>(null);
  const contactNoticeRef = useRef<HTMLSpanElement>(null);
  const [isContactNoticeTruncated, setIsContactNoticeTruncated] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const [hasContactNotes, setHasContactNotes] = useState(false);

  useEffect(() => {
    const el = noticeTextRef.current;
    if (el) {
      setIsNoticeTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [valuedEntity?.valuedEntityNotice]);

  useEffect(() => {
    const el = contactNoticeRef.current;
    if (el) {
      setIsContactNoticeTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [valuedEntity?.primaryContactNotice]);

  // Update local state when companyFolder prop changes
  const resolveCompanyFolderPath = useCallback((folderRef?: string | null) => {
    if (!folderRef?.trim()) {
      return null;
    }
    return resolveDropboxPath(folderRef, dropboxRoot);
  }, [dropboxRoot]);

  useEffect(() => {
    if (companyFolder !== undefined) {
      setCompanyFolderValue(stripDropboxRootPrefix(companyFolder || '', [dropboxRoot]));
      setSelectedCompanyFolderPath(resolveCompanyFolderPath(companyFolder));
    }
  }, [companyFolder, dropboxRoot, resolveCompanyFolderPath]);

  // When Dropbox disconnects mid-edit, exit edit mode and prompt reconnect
  useEffect(() => {
    if (dropboxConnected === false && isEditingCompanyFolder) {
      setIsEditingCompanyFolder(false);
      setIsAuthRequired(true);
    }
  }, [dropboxConnected, isEditingCompanyFolder]);

  const handleCompanyFolderSync = async () => {
    if (!onUpdateCompanyFolder) {
      return;
    }

    const folderNameOnly = companyFolderValue.trim();
    const currentRelativeFolder = stripDropboxRootPrefix(companyFolder || '', [dropboxRoot]);

    // If the value hasn't changed, just exit edit mode
    if (folderNameOnly === currentRelativeFolder) {
      setIsEditingCompanyFolder(false);
      return;
    }

    setIsSavingCompanyFolder(true);
    try {
      await onUpdateCompanyFolder(folderNameOnly, selectedCompanyFolderPath);
      setIsEditingCompanyFolder(false);
    } catch (error) {
      Logger.error('Error updating company folder', error);
    } finally {
      setIsSavingCompanyFolder(false);
    }
  };

  useEffect(() => {
    if (!valuedEntity?.valuedEntityId) {
      setHasNotes(false);
      setHasContactNotes(false);
      return;
    }

    const notesService = new AttioNotesService();

    notesService.getAllNotes(valuedEntity.valuedEntityId, 'companies').then(notes => {
      setHasNotes(notes.length > 0);
    });

    if (!valuedEntity.primaryContactId) {
      setHasContactNotes(false);
      return;
    }

    notesService.getAllNotes(valuedEntity.primaryContactId, 'people').then(notes => {
      setHasContactNotes(notes.length > 0);
    });
  }, [valuedEntity?.valuedEntityId, valuedEntity?.primaryContactId]);

  const handleCompanyFolderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCompanyFolderSync();
    } else if (e.key === 'Escape') {
      setCompanyFolderValue(stripDropboxRootPrefix(companyFolder || '', [dropboxRoot]) || suggestedCompanyFolderName || '');
      setSelectedCompanyFolderPath(resolveCompanyFolderPath(companyFolder));
      setIsEditingCompanyFolder(false);
    }
  };

  const handleCreateCompanyFolder = async () => {
    if (!onCreateCompanyFolder) {
      return;
    }
    await onCreateCompanyFolder(companyFolderValue.trim());
  };

  const handleSearchCompanyFolder = async () => {
    if (!onSearchCompanyFolder) {
      return;
    }
    try {
      await onSearchCompanyFolder(companyFolderValue.trim());
    } catch (error) {
      Logger.error('Error searching company folder', error);
    }
  };

  const handleCompanyFolderSelect = (option: DropboxFolderOption) => {
    const relativePath = stripDropboxRootPrefix(option.pathDisplay, [dropboxRoot]);
    setCompanyFolderValue(relativePath || option.name);
    setSelectedCompanyFolderPath(option.pathDisplay);
  };

  const handleCompanyFolderNameChange = (value: string) => {
    setCompanyFolderValue(value);
    setSelectedCompanyFolderPath(null);
  };

  const searchResults = companyFolderSuggestions.filter(option => Boolean(option.pathDisplay?.trim()));
  const isSearchingCompanyFolder = companyFolderStatus === 'searching';

  const statusConfig = getDropboxFolderStatusConfig(companyFolderStatus);

  // Display full path: {dropboxRoot}/{folderName}
  const displayCompanyFolderPath = companyFolder ? stripDropboxRootPrefix(companyFolder, [dropboxRoot]) : '';

  let companyFolderContent: ReactNode;
  if (isEditingCompanyFolder && !isAuthRequired) {
    companyFolderContent = (
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        <TextField
          sx={{ '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
          select
          label="Dropbox Root"
          value={dropboxRoot}
          onChange={e => onDropboxRootChange?.(e.target.value)}
          size="small"
        >
          {dropboxRootOptions.map(root => (
            <MenuItem key={root} value={root}>
              {root}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Company Folder"
          placeholder={suggestedCompanyFolderName || 'Search or enter company folder path'}
          value={companyFolderValue}
          onChange={e => handleCompanyFolderNameChange(e.target.value)}
          onKeyDown={handleCompanyFolderKeyDown}
          disabled={isSavingCompanyFolder}
          size="small"
          fullWidth
          helperText={`Path: ${dropboxRoot}/${companyFolderValue || '<folder>'}`}
        />
        <DropboxFolderSearchResults
          results={searchResults}
          dropboxRoot={dropboxRoot}
          selectedPath={selectedCompanyFolderPath}
          onSelect={handleCompanyFolderSelect}
          isLoading={isSearchingCompanyFolder}
          suggestedFolderName={suggestedCompanyFolderName}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            onClick={handleSearchCompanyFolder}
            disabled={!companyFolderValue.trim() || isSearchingCompanyFolder}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCreateCompanyFolder}
            disabled={!companyFolderValue.trim() || companyFolderStatus === 'creating'}
          >
            Create
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleCompanyFolderSync}
            disabled={!companyFolderValue.trim() || isSavingCompanyFolder || companyFolderStatus === 'syncing'}
          >
            Sync
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setCompanyFolderValue(stripDropboxRootPrefix(companyFolder || '', [dropboxRoot]) || suggestedCompanyFolderName || '');
              setSelectedCompanyFolderPath(resolveCompanyFolderPath(companyFolder));
              setIsEditingCompanyFolder(false);
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    );
  } else if (isAuthRequired && onDropboxConnect) {
    companyFolderContent = (
      <DropboxAuthRequired
        onConnect={onDropboxConnect}
        onCancel={() => setIsAuthRequired(false)}
      />
    );
  } else {
    companyFolderContent = (
      <Typography
        onClick={() => {
          if (!onUpdateCompanyFolder) {
            return;
          }
          if (dropboxConnected === false) {
            setIsAuthRequired(true);
            return;
          }
          setCompanyFolderValue(stripDropboxRootPrefix(companyFolder || '', [dropboxRoot]) || suggestedCompanyFolderName || '');
          setSelectedCompanyFolderPath(resolveCompanyFolderPath(companyFolder));
          setIsEditingCompanyFolder(true);
        }}
        sx={{
          mt: 0.5,
          width: 'fit-content',
          cursor: onUpdateCompanyFolder ? 'pointer' : 'default',
          color: onUpdateCompanyFolder ? 'primary.main' : 'text.primary',
          fontStyle: 'italic',
          '&:hover': onUpdateCompanyFolder ? { textDecoration: 'underline' } : {},
        }}
      >
        {displayCompanyFolderPath || 'Click here to set the folder path'}
      </Typography>
    );
  }

  return (
    <Section
      title="Valued Entity"
      headerAction={(
        <AttioNotesButton
          recordId={valuedEntity?.valuedEntityId}
          object="company"
          hasNotes={hasNotes}
        />
      )}
    >
      <Container>
        <HeaderRow id="company-name">
          <BusinessIcon color="primary" fontSize="small" />
          <ConditionalLoader isLoading={isLoading && !valuedEntity?.companyName} width={150}>
            {valuedEntity?.companyUrl ? (
              <Link
                href={valuedEntity.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledLinkSpan>{valuedEntity.companyName || '-'}</StyledLinkSpan>
              </Link>
            ) : (
              <StyledLinkSpan>{valuedEntity?.companyName || '-'}</StyledLinkSpan>
            )}
          </ConditionalLoader>
        </HeaderRow>
        {valuedEntity?.valuedEntityNotice && (
          <Box>
            <Typography
              ref={noticeTextRef}
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              NOTICE: {valuedEntity.valuedEntityNotice}
            </Typography>
            {isNoticeTruncated && (
              <>
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={e => setNoticeAnchorEl(e.currentTarget)}
                >
                  show more
                </Typography>
                <Popover
                  open={Boolean(noticeAnchorEl)}
                  anchorEl={noticeAnchorEl}
                  onClose={() => setNoticeAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, maxWidth: 400 }}>
                    <Typography variant="body2">NOTICE: {valuedEntity.valuedEntityNotice}</Typography>
                  </Box>
                </Popover>
              </>
            )}
          </Box>
        )}
        <InfoRow>
          <ContactSection>
            <Box>
              <Typography id="primary-contact" variant="body2" color="text.secondary">
                Primary Contact:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                <ConditionalLoader isLoading={isLoading && !valuedEntity?.primaryContact} width={150}>
                  <Box>
                    <Typography fontWeight="bold">{valuedEntity?.primaryContact || '-'}</Typography>
                  </Box>
                </ConditionalLoader>
                <AttioNotesButton
                  recordId={valuedEntity?.primaryContactId}
                  object="person"
                  hasNotes={hasContactNotes}
                />
              </Box>
            </Box>
            {valuedEntity?.primaryContactNotice && (
              <Box>
                <Typography
                  ref={contactNoticeRef}
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                  }}
                >
                  NOTICE: {valuedEntity.primaryContactNotice}
                </Typography>
                {isContactNoticeTruncated && (
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary"
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                      onClick={e => setContactNoticeAnchorEl(e.currentTarget)}
                    >
                      show more
                    </Typography>
                    <Popover
                      open={Boolean(contactNoticeAnchorEl)}
                      anchorEl={contactNoticeAnchorEl}
                      onClose={() => setContactNoticeAnchorEl(null)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                      <Box sx={{ p: 2, maxWidth: 400 }}>
                        <Typography variant="body2">NOTICE: {valuedEntity.primaryContactNotice}</Typography>
                      </Box>
                    </Popover>
                  </>
                )}
              </Box>
            )}
            <Box>
              <Typography id="category" variant="body2" color="text.secondary" mt={1}>
                Category:
              </Typography>
            </Box>
            <ConditionalLoader isLoading={isLoading && !valuedEntity?.category} width={150}>
              <CategoryBadge>{valuedEntity?.category || '-'}</CategoryBadge>
            </ConditionalLoader>
            <Box>
              <Typography id="dropbox-folder" variant="body2" color="text.secondary" mt={1}>
                Dropbox: Company Folder
              </Typography>
            </Box>
            <ConditionalLoader isLoading={isLoading} width={200}>
              {companyFolderContent}
            </ConditionalLoader>
            {!isAuthRequired && (
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" color={statusConfig.color} label={statusConfig.label} />
              </Box>
            )}
            {companyFolderMatchPaths.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Found at:
                </Typography>
                {companyFolderMatchPaths.slice(0, 3).map(path => (
                  <Typography key={path} variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {path}
                  </Typography>
                ))}
                {companyFolderMatchPaths.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{companyFolderMatchPaths.length - 3} more
                  </Typography>
                )}
              </Box>
            )}
            {companyFolderSimilarMatchesCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {companyFolderSimilarMatchesCount} similar {companyFolderSimilarMatchesCount === 1 ? 'match' : 'matches'} found. Select the correct folder from the search results.
              </Typography>
            )}
          </ContactSection>
          {(valuedEntity?.salesRep || isLoading) && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
              Sales Rep:
              </Typography>
              <Avatar
                data={[{ picture_url: valuedEntity?.salesRepImageSrc || '' }]}
                name={valuedEntity?.salesRep || ''}
                isLoading={isLoading && !valuedEntity?.salesRep}
              />
            </Box>
          )}
        </InfoRow>
      </Container>
    </Section>
  );
};

export default ValuedEntity;
